import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Clock, CheckCircle, XCircle, CalendarClock, Trash2, Search, RefreshCw, CheckSquare, MinusSquare } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  PageHeader,
  Card,
  Modal,
  FilterBar,
  FilterChip,
  type ColumnDef,
} from '@/components/ui'
import { useToast } from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ArticleEol {
  id: string
  articleId: string
  itemCode: string
  itemName: string
  groupName: string | null
  manufacturer: string | null
  purchasePriceCents: number
  stockQuantity: number
  eolDate: string | null
  eosDate: string | null
  notes: string | null
  updatedAt: string
  daysUntilEol: number | null
  daysUntilEos: number | null
  status: 'expired' | 'urgent' | 'warning' | 'ok' | 'no_date'
  egisAvailable: boolean | null
  egisCheckedAt: string | null
}

interface EolSummary {
  expiredCount: number
  urgentCount: number
  warningCount: number
  totalWithDate: number
  egisNotFoundCount: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'with_date' | 'expired' | 'urgent' | 'warning' | 'egis_not_found'

function EolStatusBadge({ status, daysUntilEol }: { status: ArticleEol['status']; daysUntilEol: number | null }) {
  if (status === 'no_date') return <span className="text-gray-400 text-xs">–</span>
  if (status === 'expired') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
      <XCircle className="w-3 h-3" />Abgelaufen
    </span>
  )
  if (status === 'urgent') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
      <AlertTriangle className="w-3 h-3" />{daysUntilEol !== null ? `${daysUntilEol}d` : 'Bald'}
    </span>
  )
  if (status === 'warning') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
      <Clock className="w-3 h-3" />{daysUntilEol !== null ? `${daysUntilEol}d` : ''}
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle className="w-3 h-3" />{daysUntilEol !== null ? `${daysUntilEol}d` : 'OK'}
    </span>
  )
}

function EgisBadge({ available, checkedAt }: { available: boolean | null; checkedAt: string | null }) {
  if (available === null || !checkedAt) {
    return <span className="text-gray-400 text-xs">Nicht geprüft</span>
  }
  if (available) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />Gefunden
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
      <XCircle className="w-3 h-3" />Nicht gefunden
    </span>
  )
}

function formatDate(d: string | null) {
  if (!d) return '–'
  return new Date(d).toLocaleDateString('de-DE')
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  article: ArticleEol
  onClose: () => void
}

function EditEolModal({ article, onClose }: EditModalProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [eolDate, setEolDate] = useState(article.eolDate ?? '')
  const [eosDate, setEosDate] = useState(article.eosDate ?? '')
  const [notes, setNotes] = useState(article.notes ?? '')

  const saveMutation = useMutation({
    mutationFn: () => apiClient.put(`/articles/eol-tracking/${article.articleId}`, {
      eolDate: eolDate || null,
      eosDate: eosDate || null,
      notes: notes || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eol-tracking'] })
      queryClient.invalidateQueries({ queryKey: ['eol-summary'] })
      toast({ title: 'Gespeichert', tone: 'success' })
      onClose()
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/articles/eol-tracking/${article.articleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eol-tracking'] })
      queryClient.invalidateQueries({ queryKey: ['eol-summary'] })
      toast({ title: 'Gelöscht', tone: 'success' })
      onClose()
    },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  return (
    <Modal open onOpenChange={(v) => { if (!v) onClose() }} title={`EOL/EOS – ${article.itemCode}`}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{article.itemName}</p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">End-of-Life (EOL)</label>
          <p className="text-xs text-gray-500 mb-1">Hardware wird eingestellt / nicht mehr produziert</p>
          <input
            type="date"
            value={eolDate}
            onChange={(e) => setEolDate(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">End-of-Support (EOS)</label>
          <p className="text-xs text-gray-500 mb-1">Herstellersupport läuft ab</p>
          <input
            type="date"
            value={eosDate}
            onChange={(e) => setEosDate(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notiz</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Nachfolgeprodukt, Hinweise, …"
            className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <div className="flex justify-between pt-2">
          <Button
            variant="danger"
            size="sm"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending || (!article.eolDate && !article.eosDate)}
          >
            <Trash2 className="w-4 h-4 mr-1" />Löschen
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Abbrechen</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ─── EGIS Scan Progress Modal ─────────────────────────────────────────────────

interface EgisScanModalProps {
  onClose: () => void
  onDone: () => void
}

function EgisScanModal({ onClose, onDone }: EgisScanModalProps) {
  const { toast } = useToast()
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle')
  const [progress, setProgress] = useState(0)
  const [total, setTotal] = useState(0)
  const [errors, setErrors] = useState<string[]>([])
  const abortRef = useRef(false)

  const start = useCallback(async () => {
    abortRef.current = false
    setPhase('running')
    setErrors([])
    setProgress(0)

    let ids: string[]
    try {
      const res = await apiClient.get<{ ids: string[] }>('/articles/eol-tracking/egis-eligible')
      ids = res.data.ids
    } catch {
      setPhase('error')
      return
    }

    setTotal(ids.length)

    const errs: string[] = []
    for (let i = 0; i < ids.length; i++) {
      if (abortRef.current) break
      try {
        await apiClient.post(`/articles/eol-tracking/${ids[i]}/egis-check`, {})
      } catch (err) {
        const msg = (err as { message?: string }).message ?? 'Unbekannter Fehler'
        errs.push(msg)
        if (errs.length > 5) break
      }
      setProgress(i + 1)
    }

    setErrors(errs)
    setPhase('done')
    onDone()
    if (errs.length === 0) {
      toast({ title: 'EGIS-Abgleich abgeschlossen', tone: 'success' })
    } else {
      toast({ title: `Abgeschlossen mit ${errs.length} Fehlern`, tone: 'warning' })
    }
  }, [toast, onDone])

  const abort = () => { abortRef.current = true }

  return (
    <Modal open onOpenChange={(v) => { if (!v) onClose() }} title="EGIS-Abgleich">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Prüft alle aktiven Artikel gegen den EGIS-Produktkatalog und markiert, ob der Artikel noch verfügbar ist.
          EOL/EOS-Daten werden dabei <strong>nicht automatisch gesetzt</strong>.
        </p>

        {phase === 'idle' && (
          <Button variant="primary" onClick={start} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" />Abgleich starten
          </Button>
        )}

        {phase === 'running' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span>Prüfe Artikel…</span>
              <span className="font-medium">{progress} / {total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                style={{ width: total > 0 ? `${(progress / total) * 100}%` : '0%' }}
              />
            </div>
            <Button variant="secondary" size="sm" onClick={abort} className="w-full">
              Abbrechen
            </Button>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
              <CheckCircle className="w-4 h-4" />
              {progress} Artikel geprüft
            </div>
            {errors.length > 0 && (
              <div className="text-xs text-red-600 bg-red-50 rounded p-2 space-y-1">
                {errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <Button variant="primary" onClick={onClose} className="w-full">Schließen</Button>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-3">
            <p className="text-sm text-red-600">Fehler beim Laden der Artikel-IDs.</p>
            <Button variant="secondary" onClick={onClose} className="w-full">Schließen</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Section ──────────────────────────────────────────────────────────────────

export function EolTrackingSection() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('with_date')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<ArticleEol | null>(null)
  const [showEgisScan, setShowEgisScan] = useState(false)
  const queryClient = useQueryClient()

  const { data: summary } = useQuery<EolSummary>({
    queryKey: ['eol-summary'],
    queryFn: () => apiClient.get('/articles/eol-tracking/summary').then((r) => r.data),
  })

  const params = new URLSearchParams()
  if (statusFilter === 'with_date') params.set('onlyWithDate', 'true')
  else if (statusFilter === 'egis_not_found') params.set('egisNotFound', 'true')
  else if (statusFilter !== 'all') params.set('status', statusFilter)
  if (search.trim()) params.set('search', search.trim())

  const { data = [], isLoading, error } = useQuery<ArticleEol[]>({
    queryKey: ['eol-tracking', statusFilter, search],
    queryFn: () => apiClient.get(`/articles/eol-tracking?${params}`).then((r) => r.data),
  })

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearch = useCallback((val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearch(val)
      if (val.trim()) setStatusFilter('all')
    }, 300)
  }, [])

  const handleEgisScanDone = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['eol-tracking'] })
    queryClient.invalidateQueries({ queryKey: ['eol-summary'] })
  }, [queryClient])

  const columns: ColumnDef<ArticleEol>[] = [
    { id: 'itemCode', header: 'Art.-Nr.', accessorKey: 'itemCode', size: 110 },
    { id: 'itemName', header: 'Bezeichnung', accessorKey: 'itemName', size: 220 },
    { id: 'manufacturer', header: 'Hersteller', accessorKey: 'manufacturer', size: 120,
      cell: ({ row }) => row.original.manufacturer ?? <span className="text-gray-400 text-xs">–</span> },
    { id: 'eolDate', header: 'EOL', accessorKey: 'eolDate', size: 95,
      cell: ({ row }) => formatDate(row.original.eolDate) },
    { id: 'eosDate', header: 'EOS', accessorKey: 'eosDate', size: 95,
      cell: ({ row }) => formatDate(row.original.eosDate) },
    { id: 'status', header: 'Status', accessorKey: 'status', size: 105,
      cell: ({ row }) => <EolStatusBadge status={row.original.status} daysUntilEol={row.original.daysUntilEol ?? row.original.daysUntilEos} /> },
    { id: 'egisAvailable', header: 'EGIS', accessorKey: 'egisAvailable', size: 120,
      cell: ({ row }) => <EgisBadge available={row.original.egisAvailable} checkedAt={row.original.egisCheckedAt} /> },
    { id: 'notes', header: 'Notiz', accessorKey: 'notes', size: 160,
      cell: ({ row }) => row.original.notes ?? <span className="text-gray-400 text-xs">–</span> },
    {
      id: 'actions', header: '', size: 120,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setEditing(row.original)}
        >
          <CalendarClock className="w-3 h-3 mr-1" />
          {row.original.eolDate || row.original.eosDate ? 'Bearbeiten' : 'Datum pflegen'}
        </Button>
      ),
    },
  ]

  if (isLoading) return <LoadingState label="EOL-Daten werden geladen…" />
  if (error) {
    const axiosErr = error as { message: string; response?: { data?: { error?: string; stack?: string } } }
    return (
      <ErrorState
        title="Fehler beim Laden der EOL-Daten"
        description={axiosErr.response?.data?.error ?? axiosErr.message}
        details={axiosErr.response?.data?.stack}
      />
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="EOL / EOS Tracking"
        description="End-of-Life und End-of-Support-Datum pro Artikel manuell pflegen. Artikel suchen, 'Datum pflegen' klicken und Datum eintragen."
        actions={
          <Button variant="secondary" size="sm" onClick={() => setShowEgisScan(true)}>
            <RefreshCw className="w-4 h-4 mr-1" />EGIS-Abgleich
          </Button>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:ring-2 hover:ring-gray-300" onClick={() => { setStatusFilter('with_date'); setSearch('') }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.totalWithDate}</div>
              <div className="text-xs text-gray-500 mt-1">Mit EOL/EOS-Datum</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-red-300 ${statusFilter === 'expired' ? 'ring-2 ring-red-400' : ''}`} onClick={() => { setStatusFilter(statusFilter === 'expired' ? 'with_date' : 'expired'); setSearch('') }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.expiredCount}</div>
              <div className="text-xs text-gray-500 mt-1">Abgelaufen</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-amber-300 ${statusFilter === 'urgent' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => { setStatusFilter(statusFilter === 'urgent' ? 'with_date' : 'urgent'); setSearch('') }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{summary.urgentCount}</div>
              <div className="text-xs text-gray-500 mt-1">&lt; 30 Tage</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-yellow-300 ${statusFilter === 'warning' ? 'ring-2 ring-yellow-400' : ''}`} onClick={() => { setStatusFilter(statusFilter === 'warning' ? 'with_date' : 'warning'); setSearch('') }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{summary.warningCount}</div>
              <div className="text-xs text-gray-500 mt-1">&lt; 90 Tage</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-red-300 ${statusFilter === 'egis_not_found' ? 'ring-2 ring-red-400' : ''}`} onClick={() => { setStatusFilter(statusFilter === 'egis_not_found' ? 'with_date' : 'egis_not_found'); setSearch('') }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.egisNotFoundCount}</div>
              <div className="text-xs text-gray-500 mt-1">Nicht in EGIS</div>
            </Card.Body>
          </Card>
        </div>
      )}

      <FilterBar>
        <div className="relative flex-shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Art.-Nr. oder Bezeichnung…"
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-8 pr-3 py-1 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 w-56"
          />
        </div>
        <div className="w-px h-5 bg-gray-200 mx-1 flex-shrink-0" />
        <FilterChip active={statusFilter === 'with_date' && !search} onRemove={() => { setStatusFilter('with_date'); setSearch('') }}>
          Mit Datum
        </FilterChip>
        <FilterChip active={statusFilter === 'all' && !search} onRemove={() => { setStatusFilter('all'); setSearch('') }}>
          Alle Artikel
        </FilterChip>
        <FilterChip active={statusFilter === 'expired'} onRemove={() => setStatusFilter(statusFilter === 'expired' ? 'with_date' : 'expired')}>
          Abgelaufen
        </FilterChip>
        <FilterChip active={statusFilter === 'urgent'} onRemove={() => setStatusFilter(statusFilter === 'urgent' ? 'with_date' : 'urgent')}>
          Dringend
        </FilterChip>
        <FilterChip active={statusFilter === 'warning'} onRemove={() => setStatusFilter(statusFilter === 'warning' ? 'with_date' : 'warning')}>
          Warnung
        </FilterChip>
        <FilterChip
          active={statusFilter === 'egis_not_found'}
          leadingIcon={<MinusSquare />}
          onRemove={() => setStatusFilter(statusFilter === 'egis_not_found' ? 'with_date' : 'egis_not_found')}
        >
          Nicht in EGIS
        </FilterChip>
        <FilterChip
          active={statusFilter === 'all' && false}
          leadingIcon={<CheckSquare />}
          onRemove={() => {}}
          onClick={() => setShowEgisScan(true)}
        >
          EGIS prüfen
        </FilterChip>
      </FilterBar>

      {data.length === 0 ? (
        statusFilter === 'with_date' && !search ? (
          <EmptyState
            title="Noch keine EOL/EOS-Daten"
            description="Suche nach einem Artikel (Art.-Nr. oder Bezeichnung) und klicke 'Datum pflegen' um EOL/EOS-Daten zu hinterlegen."
          />
        ) : (
          <EmptyState title="Keine Artikel gefunden" description="Keine Artikel mit diesen Filterkriterien gefunden." />
        )
      ) : (
        <DataTable columns={columns} data={data} pagination={{ pageSize: 50 }} searchable={false} />
      )}

      {editing && <EditEolModal article={editing} onClose={() => setEditing(null)} />}
      {showEgisScan && (
        <EgisScanModal
          onClose={() => setShowEgisScan(false)}
          onDone={handleEgisScanDone}
        />
      )}
    </div>
  )
}
